import { useEffect, useState } from "react";
import { IconAlertOctagon, IconChevronDown, IconChevronRight, IconRefresh, IconRefreshAlert } from "@tabler/icons-react";
import {
  AdminPageHeader,
  Badge,
  Button,
  Card,
  EmptyState,
  Group,
  IconAction,
  PaginadorTabla,
  Skeleton,
  Stack,
  Switch,
  TablaSkeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import {
  useEventosFallidos,
  useReprocesarEventoFallido,
  useResumenEventosFallidos,
} from "../hooks/use-eventos-fallidos";
import type { EventoFallido } from "../services/eventos-fallidos-service";
import { COLUMNAS_EVENTOS_SKELETON, ModulosEventosSkeleton } from "./EventosFallidosSkeleton";

const PAGE_SIZE = 15;

const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleString("es-MX") : "—");

/**
 * Hotspot 55 · `/admin/eventos-fallidos`. Eventos de integración del outbox de cada módulo que
 * fallaron al procesarse: los que siguen reintentándose con espera creciente (1 min, 5 min,
 * 15 min, 1 h…) y los AGOTADOS (sin más intentos automáticos). Muestra el error y el contenido
 * del evento, y permite reprocesarlo (vuelve a la cola con los intentos en cero).
 */
export function AdminEventosFallidosPage() {
  const resumen = useResumenEventosFallidos();
  const modulos = resumen.data ?? [];
  const [modulo, setModulo] = useState("");
  const [soloAgotados, setSoloAgotados] = useState(false);
  const [page, setPage] = useState(1);

  // Selección inicial: el primer módulo con errores (o el primero de la lista).
  useEffect(() => {
    if (modulo || modulos.length === 0) return;
    const conErrores = modulos.find((m) => m.agotados + m.enReintento > 0);
    setModulo((conErrores ?? modulos[0]).modulo);
  }, [modulo, modulos]);

  useEffect(() => {
    setPage(1);
  }, [modulo, soloAgotados]);

  const lista = useEventosFallidos({ modulo, soloAgotados, page, pageSize: PAGE_SIZE });
  const eventos = lista.data?.items ?? [];
  const total = lista.data?.total ?? 0;
  // Primera carga · la lista espera al resumen (elige el módulo inicial) y luego a
  // su propia consulta. Si el resumen llegó sin módulos no hay nada que esperar.
  const cargandoLista = lista.isPending && (resumen.isPending || modulos.length > 0);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <AdminPageHeader
        title="Eventos fallidos"
        description="Eventos de integración que no se pudieron procesar. Se reintentan solos con espera creciente; al agotar los intentos quedan aquí para reprocesarlos."
        actions={
          <IconAction
            label="Refrescar"
            variant="default"
            size="lg"
            loading={resumen.isFetching || lista.isFetching}
            onClick={() => {
              void resumen.refetch();
              void lista.refetch();
            }}
          >
            <IconRefresh size={16} />
          </IconAction>
        }
      />
      <Stack gap="md">
        {resumen.isPending ? (
          <ModulosEventosSkeleton />
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
            {modulos.map((m) => {
              const seleccionado = m.modulo === modulo;
              return (
                <button
                  key={m.modulo}
                  type="button"
                  onClick={() => setModulo(m.modulo)}
                  aria-pressed={seleccionado}
                  className={cn(
                    "rounded-lg border p-3 text-left transition-colors",
                    seleccionado ? "border-multiva-600 bg-multiva-50 dark:bg-multiva-900/30" : "border-border hover:bg-muted"
                  )}
                >
                  <Text size="sm" fw={600} truncate>
                    {m.modulo}
                  </Text>
                  <Group gap={6} className="mt-1">
                    <Badge size="xs" color={m.agotados > 0 ? "peligro" : "slate"}>
                      {m.agotados} agotados
                    </Badge>
                    <Badge size="xs" color={m.enReintento > 0 ? "advertencia" : "slate"}>
                      {m.enReintento} en reintento
                    </Badge>
                  </Group>
                </button>
              );
            })}
          </div>
        )}

        <Card padding="lg">
          <Stack gap="md">
            <Group justify="space-between" className="flex-wrap" gap="sm">
              {cargandoLista && !modulo ? (
                <Skeleton height={16} width={160} aria-hidden="true" />
              ) : (
                <Text fw={600}>{modulo ? `Módulo ${modulo}` : "Selecciona un módulo"}</Text>
              )}
              <Switch
                label="Solo agotados"
                checked={soloAgotados}
                onCheckedChange={(v) => setSoloAgotados(v === true)}
              />
            </Group>
            <PaginadorTabla cargando={cargandoLista} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
            {total === 0 && !cargandoLista ? (
              <EmptyState
                icon={IconRefreshAlert}
                title="Sin eventos con error"
                description="Todos los eventos de este módulo se procesaron correctamente."
                compact
              />
            ) : (
              <div className="overflow-x-auto">
                <Table minWidth={900}>
                  <Thead>
                    <Tr>
                      <Th />
                      <Th>Evento</Th>
                      <Th>Ocurrió</Th>
                      <Th>Intentos</Th>
                      <Th>Estado</Th>
                      <Th>Próximo intento</Th>
                      <Th>Error</Th>
                      <Th align="right">Acción</Th>
                    </Tr>
                  </Thead>
                  {cargandoLista ? (
                    <TablaSkeleton dentroDeTabla columnas={COLUMNAS_EVENTOS_SKELETON} filas={PAGE_SIZE} />
                  ) : (
                    <Tbody>
                      {eventos.map((e) => (
                        <FilaEvento key={e.id} evento={e} />
                      ))}
                    </Tbody>
                  )}
                </Table>
              </div>
            )}
            <PaginadorTabla cargando={cargandoLista} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="eventos" />
          </Stack>
        </Card>
      </Stack>
    </div>
  );
}

function FilaEvento({ evento }: { evento: EventoFallido }) {
  const [abierto, setAbierto] = useState(false);
  const reprocesar = useReprocesarEventoFallido();
  const agotado = evento.estado === "Agotado";

  return (
    <>
      <Tr>
        <Td>
          <IconAction label={abierto ? "Ocultar detalle" : "Ver detalle"} variant="subtle" onClick={() => setAbierto((v) => !v)}>
            {abierto ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          </IconAction>
        </Td>
        <Td>
          <Text size="sm" fw={600}>
            {evento.eventoNombre}
          </Text>
          <Text size="xs" c="dimmed" ff="mono">
            {evento.id}
          </Text>
        </Td>
        <Td>
          <Text size="sm">{fecha(evento.occurredAt)}</Text>
        </Td>
        <Td>
          <Text size="sm">
            {evento.retryCount} / {evento.maxRetries}
          </Text>
        </Td>
        <Td>
          <Badge size="xs" color={agotado ? "peligro" : "advertencia"}>
            {agotado ? "Agotado" : "Reintentando"}
          </Badge>
        </Td>
        <Td>
          <Text size="sm">{agotado ? "—" : fecha(evento.nextAttemptAt)}</Text>
        </Td>
        <Td className="max-w-80">
          <Text size="xs" c="peligro-800" truncate>
            {evento.error ?? "—"}
          </Text>
        </Td>
        <Td align="right">
          <Button
            variant="default"
            size="xs"
            leftSection={<IconRefresh size={12} />}
            loading={reprocesar.isPending}
            onClick={() => reprocesar.mutate({ modulo: evento.modulo, id: evento.id })}
          >
            Reprocesar
          </Button>
        </Td>
      </Tr>
      {abierto ? (
        <Tr>
          <Td colSpan={8}>
            <Stack gap="xs">
              <Group gap={6}>
                <IconAlertOctagon size={14} className="text-peligro-600" />
                <Text size="xs" fw={600}>
                  Error del último intento
                </Text>
              </Group>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs">
                {evento.error ?? "(sin detalle)"}
              </pre>
              <Text size="xs" fw={600}>
                Evento ({evento.eventType.split(",")[0]})
              </Text>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 font-mono text-xs">
                {formatearPayload(evento.payload)}
              </pre>
            </Stack>
          </Td>
        </Tr>
      ) : null}
    </>
  );
}

function formatearPayload(payload: string): string {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}
